// Account ↔ browser sync (ADR 0013): server state pulled at sign-in, outbox of the activities recorded while the
// server was unreachable, automatic upload of profile and plan changes, local caches emptied at sign-out.
import { db, ACCOUNT_STORES } from './db.js';
import type { LocalActivity } from './activities.js';
import { state, update, onChange, resetAccountData, type State } from './store.js';
import { api, isLoggedIn, updateUser, onAuthChange, ApiError } from './api.js';
import type { MeResponse, ActivityMeta, Profile, FtpState } from '@gruvetto/contracts';
import type { Samples } from '@gruvetto/core';
import { uploadRecording } from './recordings.js';
import { refreshRating } from './rating.js';
import { clearEventCache } from './events.js';
import { flushReplays } from './replays.js';
import { pullCollection, clearCollection } from './collection.js';

export interface SyncStatus { state: 'idle' | 'syncing' | 'error' | 'offline'; at: number | null; message?: string }
export const syncStatus: SyncStatus = { state: 'idle', at: null };
const listeners = new Set<() => void>();
export const onSyncChange = (fn: () => void) => { listeners.add(fn); return () => listeners.delete(fn); };
const setStatus = (s: SyncStatus['state'], message?: string) => { syncStatus.state = s; syncStatus.message = message; if (s === 'idle') syncStatus.at = Date.now(); for (const f of listeners) f(); };

let pushedProfile = '', pushedPlan = '';
/** What the player edits of the profile, in a fixed order: the FTP is the server's (ADR 0027) and never goes up. */
const editable = (p: State['profile']) => JSON.stringify({ name: p.name, mass: p.mass, color: p.color, look: p.look, culture: p.culture, country: p.country ?? null });

/** Takes the server's FTP estimate in (after /me, a saved ride or a weight change). */
export function adoptFtp(ftp: FtpState) {
  update(s => { s.ftp = ftp; s.profile.ftp = ftp.ftp; });
}
/** Upload of a local profile or plan change, due 800 ms after it (null: nothing waiting), and whether one is in flight. */
let timer: ReturnType<typeof setTimeout> | null = null, pushing = false;

/** Replaces the local state with the account's. A profile or plan change still waiting to be uploaded is kept: it goes up next. */
export async function pullMe(): Promise<MeResponse> {
  const me = await api<MeResponse>('/me');
  pushedProfile = editable(me.profile); pushedPlan = JSON.stringify(me.plan);
  const pending = timer !== null || pushing;
  update(s => { if (!pending) { s.profile = { ...s.profile, ...me.profile }; s.plan = me.plan as State['plan']; } s.ftp = me.ftp; s.profile.ftp = me.ftp.ftp; s.progress = me.progress as State['progress']; s.totals = me.totals; s.club = me.club ?? null; s.onboarded = true; });
  updateUser(me.user);
  return me;
}

/**
 * Uploads the activities of the outbox, then their replays. Each goes up as a recording (ADR 0019): the server resumes
 * one it already holds part of (the ride's live upload before the network dropped), so nothing is sent twice.
 * A ride the server refuses stays in the outbox for the next sync and does not hold back the others; the first refusal
 * is thrown once all have been tried. A network drop stops at once: the rides behind it would fail the same way.
 */
export async function flushQueue(): Promise<number> {
  const pending = (await db.all<LocalActivity>('activities')).filter(a => a.synced === false);
  let n = 0, refused: unknown = null;
  for (const a of pending) {
    const samples = (await db.get('samples', a.id))?.data as Samples | undefined;
    if (!samples) continue;
    let r;
    try {
      r = await uploadRecording(a.id, a.date, a.meta as ActivityMeta, samples);
    } catch (e) {
      if (e instanceof ApiError && e.offline) throw e;
      refused ??= e;
      continue;
    }
    if ('activity' in r) await db.put('activities', { ...a, ...r.activity, ...r.activity.meta, synced: true });
    else await db.put('activities', { ...a, synced: true });
    n++;
  }
  await flushReplays();
  if (refused) throw refused;
  return n;
}

/** Activities waiting in the outbox (shown before a sign-out, which would drop them). */
export async function pendingCount(): Promise<number> {
  return (await db.all<LocalActivity>('activities')).filter(a => a.synced === false).length;
}

/** The sync in flight: a second call joins it rather than sending the outbox again. */
let syncing: Promise<void> | null = null;

/** Full sync (at startup and after sign-in). A refused ride in the outbox does not keep the account from refreshing. */
export function syncNow(): Promise<void> {
  if (!isLoggedIn()) return Promise.resolve();
  syncing ??= runSync().finally(() => { syncing = null; });
  return syncing;
}

async function runSync() {
  setStatus('syncing');
  const failed = (e: unknown) => setStatus(e instanceof ApiError && e.offline ? 'offline' : 'error', (e as Error).message);
  let refused: unknown = null;
  try { await flushQueue(); } catch (e) { if (e instanceof ApiError && e.offline) return failed(e); refused = e; }
  try { await pullMe(); await pullCollection(); await refreshRating(); } catch (e) { return failed(e); }
  if (refused) failed(refused);
  else setStatus('idle');
}

/** Sign-out: nothing of the account stays in this browser (device settings and tour history do). */
export async function clearLocalData() {
  resetAccountData();
  clearEventCache();
  clearCollection();
  for (const s of ACCOUNT_STORES) await db.clear(s);
}

/** Starts the automatic upload of profile and plan changes. */
export function startSync() {
  onChange(() => {
    if (!isLoggedIn()) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      timer = null; pushing = true;
      const p = state.profile, prof = editable(p);
      try {
        if (prof !== pushedProfile) {
          const saved = await api<Profile>('/me/profile', { method: 'PATCH', body: JSON.parse(prof) });
          pushedProfile = prof; updateUser({ name: p.name });
          // the provisional FTP follows the weight
          if (saved.ftp !== state.profile.ftp) await pullMe();
        }
        if (JSON.stringify(state.plan) !== pushedPlan) { await api('/me/plan', { method: 'PUT', body: state.plan }); pushedPlan = JSON.stringify(state.plan); }
      } catch (e) { setStatus(e instanceof ApiError && e.offline ? 'offline' : 'error', (e as Error).message); }
      finally { pushing = false; }
    }, 800);
  });
  onAuthChange(s => { if (!s) { pushedProfile = ''; pushedPlan = ''; setStatus('idle'); clearLocalData().catch(() => {}); } });
  if (isLoggedIn()) syncNow();
}
